/**
 * ダークモードの切り替えユーティリティ。
 *
 * 仕様:
 * - <html data-theme="light" | "dark"> で表示テーマを制御する。
 * - data-theme を明示的に持たない状態（＝ユーザーが一度も切り替えていない）では、
 *   OS 設定（prefers-color-scheme）に自動追随する（global.css 側の @media で実現）。
 * - ユーザーが手動で切り替えたら localStorage(STORAGE_KEY) に保存し、以後はそれを優先する。
 * - FOUC 対策の初期反映は index.html の head 内インラインスクリプトで行う（このモジュールと
 *   同じロジックを最小限だけ複製している。挙動を変えたら index.html 側も合わせて直すこと）。
 */

export type Theme = 'light' | 'dark';
/** data-theme 未設定（OS 追随）を含めた設定値。 */
export type ThemePreference = Theme | 'system';

const STORAGE_KEY = 'vrma-hub:theme';

function isTheme(value: string | null): value is Theme {
  return value === 'light' || value === 'dark';
}

/** OS の配色設定（prefers-color-scheme）を返す。 */
function getSystemTheme(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** localStorage に保存されているユーザーの明示的な選択（無ければ 'system'）。 */
export function getStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isTheme(stored) ? stored : 'system';
}

/** 現在画面に反映されている実効テーマ（data-theme があればそれ、無ければ OS 設定）。 */
export function getEffectiveTheme(): Theme {
  if (typeof document === 'undefined') return 'light';
  const attr = document.documentElement.getAttribute('data-theme');
  return isTheme(attr) ? attr : getSystemTheme();
}

/** <html data-theme> と localStorage を更新する。 */
export function setTheme(pref: ThemePreference): void {
  if (typeof document === 'undefined') return;
  if (pref === 'system') {
    document.documentElement.removeAttribute('data-theme');
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    document.documentElement.setAttribute('data-theme', pref);
    window.localStorage.setItem(STORAGE_KEY, pref);
  }
  notify();
}

/** 現在の実効テーマを反転させて確定保存する。 */
export function toggleTheme(): Theme {
  const next: Theme = getEffectiveTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

type Listener = (theme: Theme) => void;
const listeners = new Set<Listener>();

function notify(): void {
  const theme = getEffectiveTheme();
  listeners.forEach((fn) => fn(theme));
}

let mediaListenerAttached = false;
function ensureMediaListener(): void {
  if (mediaListenerAttached || typeof window === 'undefined' || !window.matchMedia) return;
  mediaListenerAttached = true;
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  // data-theme が未設定（OS 追随）のときだけ、OS 設定の変化を購読者へ伝える。
  media.addEventListener('change', () => {
    if (getStoredPreference() === 'system') notify();
  });
}

/**
 * 実効テーマの変化（手動切り替え・OS 設定変化のどちらも）を購読する。
 * 戻り値の関数を呼ぶと購読解除。
 */
export function subscribeTheme(listener: Listener): () => void {
  ensureMediaListener();
  listeners.add(listener);
  return () => listeners.delete(listener);
}
